import type { LegalAction } from '../../api-client.js';
import {
  forwardLine,
  goalXY,
  zoneToXY,
  type FieldLane,
  type FieldLine,
} from '../../lib/afl/field-coords.js';

/**
 * Click-to-move disposal targets.
 *
 * Given the disposal actions of the player you've picked from your hand,
 * this lights up every place that player can send the ball — one marker
 * per destination cell, plus a goal marker for a shot (md+ only: on phones
 * the shot lives as pinned gold SHOOT buttons above the hand, where kick
 * pills can never bury it). Tap a marker to dispose. The only cell two
 * verbs can share is "one straight forward" (RUN carries it there; a
 * forward KICK lands it there) — that marker opens a tiny chooser instead
 * of firing immediately. Everything else is a single tap.
 *
 * Coordinates come from field-coords so markers sit exactly where the ball
 * and player cards are drawn. Rendered inside the Field's `targets` layer.
 */

type Ball = { line: FieldLine; lane: FieldLane } | null | undefined;

type Verb = 'run' | 'handpass' | 'kick' | 'shot';

const VERB_LABEL: Record<Verb, string> = {
  run: 'Run',
  handpass: 'Handpass',
  kick: 'Kick', // every kick opens a marking contest at the destination
  shot: 'Shot',
};

function verbOf(action: LegalAction): Verb | null {
  const d = action.descriptorId ?? '';
  if (d.startsWith('afl:run:')) return 'run';
  if (d.startsWith('afl:handpass:')) return 'handpass';
  if (d.startsWith('afl:kick:')) return 'kick';
  if (d.startsWith('afl:shot:')) return 'shot';
  return null;
}

/**
 * A kick's destination cell. The descriptor is the canonical source —
 * `afl:kick:<playerId>:<line>:<lane>` (the same string the engine parses
 * back) — so we read it from there rather than `sourceRef`, which isn't
 * always carried through to the client. Falls back to sourceRef just in
 * case the id shape ever changes. Card ids are colon-free cuids, so the
 * positional split is safe.
 */
function kickDest(action: LegalAction): { line: FieldLine; lane: FieldLane } | null {
  const id = action.descriptorId ?? '';
  const prefix = id.startsWith('afl:kick:') ? 'afl:kick:' : null;
  if (prefix) {
    const [, line, lane] = id.slice(prefix.length).split(':');
    if (line && lane) return { line: line as FieldLine, lane: lane as FieldLane };
  }
  const ref = action.sourceRef ?? {};
  const line = ref['destLine'] as FieldLine | undefined;
  const lane = ref['destLane'] as FieldLane | undefined;
  return line && lane ? { line, lane } : null;
}

/** A shot's card-specific suffix, e.g. "(Torpedo)" — empty for a plain shot. */
function shotSuffix(action: LegalAction): string {
  const m = (action.label ?? '').match(/\(([^)]+)\)\s*$/);
  return m ? ` ${m[1]}` : '';
}

/**
 * The modifier card riding this disposal (Contested Mark / Shot on Goal / Dash
 * / Handball Pass …), named by the engine in `sourceRef.mod`. Lets a cell that
 * offers BOTH a plain disposal and a modified one render two distinct pills
 * ("Kick" vs "Kick · Contested Mark") instead of two identical ones.
 */
function modName(action: LegalAction): string {
  const m = action.sourceRef?.['mod'];
  return typeof m === 'string' ? m : '';
}

type Choice = { action: LegalAction; verb: Verb };
type Target = { key: string; x: number; y: number; goal: boolean; choices: Choice[] };

function buildTargets(actions: LegalAction[], ball: Ball, attackingRight: boolean): Target[] {
  const byKey = new Map<string, Target>();
  const add = (key: string, x: number, y: number, goal: boolean, choice: Choice) => {
    const t = byKey.get(key) ?? { key, x, y, goal, choices: [] };
    t.choices.push(choice);
    byKey.set(key, t);
  };

  for (const action of actions) {
    const verb = verbOf(action);
    if (!verb) continue;

    if (verb === 'shot') {
      const { x, y } = goalXY(attackingRight);
      add('goal', x, y, true, { action, verb });
      continue;
    }
    if (verb === 'kick') {
      const dest = kickDest(action);
      if (!dest) continue;
      const { x, y } = zoneToXY(dest.line, dest.lane, attackingRight);
      add(`${dest.line}:${dest.lane}`, x, y, false, { action, verb });
      continue;
    }
    if (!ball) continue; // run / handpass need the current ball cell
    if (verb === 'handpass') {
      const { x, y } = zoneToXY(ball.line, ball.lane, attackingRight);
      add(`${ball.line}:${ball.lane}`, x, y, false, { action, verb });
    } else {
      // run → one line forward, same lane. At f50 it can't advance, so drop it.
      const line = forwardLine(ball.line);
      if (line === ball.line) continue;
      const { x, y } = zoneToXY(line, ball.lane, attackingRight);
      add(`${line}:${ball.lane}`, x, y, false, { action, verb });
    }
  }

  return [...byKey.values()];
}

/** Solid, legible pill colours per verb — every option is explicit. The four
 *  hues stay distinct: slate run, leaf handpass, azure kick, amber shot (gold
 *  for the shot at goal). The bright fills carry dark ink for contrast. */
const VERB_TONE: Record<Verb, string> = {
  run: 'border-ec-ruck bg-[rgba(62,68,77,0.92)] text-ec-face',
  handpass: 'border-ec-defence bg-[rgba(85,167,47,0.92)] text-ec-ink',
  kick: 'border-ec-oxblood-2 bg-[rgba(39,147,211,0.92)] text-ec-ink', // opens a marking contest
  shot: 'border-ec-brass-2 bg-[rgba(245,168,28,0.95)] text-ec-ink',
};

export function DisposalLayer({
  actions,
  ball,
  attackingRight,
  busy,
  onChoose,
}: {
  actions: LegalAction[];
  ball: Ball;
  attackingRight: boolean;
  busy: boolean;
  onChoose: (action: LegalAction) => void;
}) {
  const targets = buildTargets(actions, ball, attackingRight);
  if (targets.length === 0) return null;

  // Goal/shot markers render FIRST so the field's KICK markers paint on TOP of
  // them — a forward kick to the cell just inside the goal (f50 corridor) sits
  // almost on top of the shot marker and must stay visible/tappable rather than
  // be buried under the wide shot pills.
  const ordered = [...targets].sort((a, b) => Number(b.goal) - Number(a.goal));

  return (
    <>
      {ordered.map((t) => (
        <TargetMarker key={t.key} target={t} busy={busy} onChoose={onChoose} />
      ))}
    </>
  );
}

/**
 * Every destination renders as one or more EXPLICIT labelled pills — no
 * hidden chooser. The only multi-verb cell ("one straight forward") shows a
 * "Run" pill and a "Kick" pill side by side, so both disposals are directly
 * tappable rather than buried behind a tap.
 */
function TargetMarker({
  target,
  busy,
  onChoose,
}: {
  target: Target;
  busy: boolean;
  onChoose: (action: LegalAction) => void;
}) {
  // The shot marker hugs the attacking-goal edge and stacks its options
  // VERTICALLY, so its (sometimes wide) pills don't sprawl back across the
  // forward kick markers and hide them. Field markers stay centred on their cell.
  const atGoal = target.goal;
  const right = target.x > 50;
  return (
    <div
      className={`pointer-events-auto absolute -translate-y-1/2 animate-pop-in gap-0.5 motion-reduce:animate-none ${
        atGoal
          ? // Goal (shot) pills are a DESKTOP-ONLY affordance: on phones the
            // pinned gold SHOOT buttons above the hand are the shot control
            // (owner mobile-first rule — the f50 kick pills were burying the
            // shot marker on small screens, making goals untappable).
            `hidden md:flex md:flex-col ${right ? 'md:items-end' : 'md:items-start'}`
          : // On phones, same-cell options stack VERTICALLY — the lane gap
            // (taller) absorbs a two-pill stack better than the line gap
            // (narrower) absorbs a two-pill row, so pairs like "Run"+"Kick"
            // stop sprawling sideways over the neighbouring cell's pill.
            'flex -translate-x-1/2 flex-col items-center sm:flex-row'
      }`}
      style={
        atGoal
          ? right
            ? { right: '1.5%', top: `${target.y}%` }
            : { left: '1.5%', top: `${target.y}%` }
          : { left: `${target.x}%`, top: `${target.y}%` }
      }
    >
      {target.choices.map(({ action, verb }, i) => {
        const mod = modName(action);
        const verbWord = verb === 'shot' ? 'Shoot' : VERB_LABEL[verb];
        const base = verb === 'shot' ? `Shoot${shotSuffix(action)}` : VERB_LABEL[verb];
        const text = mod ? `${verbWord} · ${mod}` : base;
        return (
          <button
            key={action.descriptorId}
            type="button"
            disabled={busy}
            onClick={() => onChoose(action)}
            aria-label={`${text}${target.goal ? ' for goal' : ''}`}
            data-drop-action={action.descriptorId}
            // Breathing pulse invites a tap or a card drop; the stagger keeps
            // neighbouring pills from throbbing in lockstep.
            style={{ animationDelay: `${(i * 137) % 400}ms` }}
            // Compact on phones (the cells are physically tiny there) but with
            // a minimum tappable footprint; full size from sm up.
            className={`inline-flex min-h-[1.75rem] min-w-[2.5rem] animate-target-pulse items-center justify-center whitespace-nowrap rounded-full border-2 px-1.5 py-1 text-[10px] font-bold leading-none shadow-lg shadow-black/50 backdrop-blur-sm transition hover:scale-125 disabled:opacity-40 motion-reduce:animate-none sm:px-2 sm:text-[11px] ${VERB_TONE[verb]}`}
          >
            {target.goal ? `🥅 ${text}` : text}
          </button>
        );
      })}
    </div>
  );
}
